import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { useAppStore } from "../store/useAppStore";
import { Plus, Search, Building, Upload, AlertCircle, Sparkles, MapPin, RefreshCw, ChevronDown, Info } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { formatDistanceToNow, parseISO } from "date-fns";
import { AddOpportunityDialog } from "../components/AddOpportunityDialog";
import { toast } from "sonner";
import { DEFAULT_CIVIC_CITIES, fetchJson, getCivicCityName, loadCivicCities } from "../lib/civicSignals";
import { apiJson } from "../lib/api";
import { PropertySnapshotCard } from "../components/discovery/PropertySnapshotCard";
import type { DiscoverySource } from "../types";

export default function Opportunities() {
  const { opportunities, setOpportunities, addOpportunity } = useAppStore();
  const navigate = useNavigate();
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState("");
  const [isSyncing, setIsSyncing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<{ imported: number; scanned: number; duplicates: number } | null>(null);
  const [selectedCity, setSelectedCity] = useState("nyc");
  const [cities, setCities] = useState<{ id: string, name: string }[]>([]);

  const [convertingId, setConvertingId] = useState<string | null>(null);

  const getCsvCell = (row: Record<string, unknown>, keys: string[]) => {
    for (const key of keys) {
      const value = row[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
    return "";
  };

  const splitTags = (value: unknown) =>
    String(value || "")
      .split(/[,;|]/)
      .map((tag) => tag.trim())
      .filter(Boolean);

  const parseScore = (value: unknown) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.max(0, Math.min(100, Math.round(value)));
    }

    const raw = String(value || "").trim();
    if (!raw) return 75;

    const parsed = Number(raw.replace(/[^0-9.-]+/g, ""));
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.min(100, Math.round(parsed)));
    }

    return 75;
  };

  const normalizeSource = (value: unknown): DiscoverySource => {
    const raw = String(value || "").trim().toLowerCase();
    if (raw.includes("public")) return "public_data";
    if (raw.includes("lead")) return "lead";
    if (raw.includes("signal")) return "signal";
    if (raw.includes("map")) return "map";
    if (raw.includes("manual")) return "manual";
    if (raw.includes("opportunity")) return "opportunity";
    return "csv";
  };

  const normalizeOpportunityKey = (value: string) => value.trim().toLowerCase();

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportSummary(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let imported = 0;
        let skippedDuplicates = 0;
        const existingKeys = new Set(
          opportunities
            .map((opp) => normalizeOpportunityKey(opp.propertyAddress))
            .filter(Boolean)
        );

        try {
          for (const row of results.data as Record<string, unknown>[]) {
            const propertyAddress = String(
              getCsvCell(row, ["Property Address", "Address", "Property", "Site Address", "Street Address", "Location"]),
            ).trim();

            if (!propertyAddress) continue;

            const key = normalizeOpportunityKey(propertyAddress);
            if (key && existingKeys.has(key)) {
              skippedDuplicates += 1;
              continue;
            }
            if (key) existingKeys.add(key);

            const notes = String(getCsvCell(row, ["Notes", "Note", "Description", "Reason", "Motivation"])) || "Imported from CSV";
            const importedTags = splitTags(getCsvCell(row, ["Tags", "Tag", "Category", "Categories", "Labels"]));
            const source = normalizeSource(getCsvCell(row, ["Source", "Lead Source", "Discovery Source", "Origin"]));
            const score = parseScore(getCsvCell(row, ["Opportunity Score", "Score", "Priority", "Rank"]));

            const created = await addOpportunity({
              propertyAddress,
              notes,
              opportunityScore: score,
              source,
              tags: importedTags.length ? importedTags : ["csv-import"],
            });
            if (created) {
              imported += 1;
            } else {
              skippedDuplicates += 1;
            }
          }

          setImportSummary({
            imported,
            scanned: Array.isArray(results.data) ? results.data.length : 0,
            duplicates: skippedDuplicates,
          });
          toast.success(
            `Imported ${imported} opportunities from CSV${skippedDuplicates ? ` (${skippedDuplicates} duplicates skipped)` : ""}.`
          );
        } catch (error) {
          console.error(error);
          toast.error("Failed to import opportunities from CSV.");
        } finally {
          setIsImporting(false);
          e.target.value = "";
        }
      },
      error: (error) => {
        console.error(error);
        setIsImporting(false);
        e.target.value = "";
        toast.error("Failed to parse CSV file.");
      },
    });
  };

  useEffect(() => {
    let cancelled = false;

    const loadCities = async () => {
      const loadedCities = await loadCivicCities();
      if (!cancelled) {
        setCities(loadedCities);
      }
    };

    void loadCities();

    const interval = window.setInterval(async () => {
      const res = await fetchJson<{ success?: boolean; count?: number }>("/api/cron-status");
      if (res?.success && typeof res.count === "number" && res.count > 0) {
        toast.success(`Background Mining: Found ${res.count} new potential signals.`, { duration: 5000 });
        // In a real app, we'd trigger a refetch or push them if they applied to this user's area
      }
    }, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const handleConvertToLead = async (e: React.MouseEvent, opp: any) => {
    e.stopPropagation();
    setConvertingId(opp.id);
    try {
      let ownerName = "PUBLIC RECORD OWNER";
      let skipTraceNotes = "";

      const trace = await apiJson<{ success?: boolean; data?: any; error?: string }>("/api/skip-trace", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address: opp.propertyAddress }),
      });

      if (trace.success && trace.data) {
        ownerName = trace.data.ownerHint || ownerName;
        skipTraceNotes = [
          `Manual skip-trace workflow for ${opp.propertyAddress}`,
          trace.data.locationHint ? `Jurisdiction: ${trace.data.locationHint}` : null,
          "",
          "Steps:",
          ...(trace.data.steps || []).map((step: string) => `- ${step}`),
          "",
          "Resources:",
          ...(trace.data.resources || []).map((resource: any) => `- ${resource.label}: ${resource.href}`),
        ].filter(Boolean).join("\n");
        toast.success("Loaded free skip-trace workflow");
      } else {
        toast.info("Skip trace workflow unavailable. Used defaults.");
      }

      await useAppStore.getState().addLead({
        sellerName: ownerName,
        phone: "",
        propertyAddress: opp.propertyAddress,
        askingPrice: null,
        motivation: "Public distress signal detected",
        status: "New Lead",
        leadSource: opp.source,
        tags: ["arcgis", "property-data", "needs-skiptrace", ...(opp.tags || [])],
        notes: [opp.notes, `Converted from Opportunity Score: ${opp.opportunityScore}`, skipTraceNotes].filter(Boolean).join("\n\n"),
      });

      const userId = useAppStore.getState().userId;
      if (userId) {
        useAppStore.setState((s) => ({ opportunities: s.opportunities.filter((o) => o.id !== opp.id) }));
        import("firebase/firestore").then(({ deleteDoc, doc }) => {
          import("../lib/firebase").then(({ db }) => {
            deleteDoc(doc(db, `users/${userId}/opportunities/${opp.id}`));
          });
        });
      }
    } catch (e) {
      toast.error("Failed to convert opportunity");
    } finally {
      setConvertingId(null);
    }
  };

  const handleSyncPublicData = async () => {
    setIsSyncing(true);
    try {
      const response = await apiJson<{ data?: any[]; success?: boolean; error?: string }>("/api/trigger-public-data-sync", { 
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ cityId: selectedCity })
      });

      const data = Array.isArray(response?.data) ? response.data : [];
      let imported = 0;
      let skippedDuplicates = 0;
      for (const opp of data) {
         const created = await addOpportunity(opp);
         if (created) {
           imported += 1;
         } else {
           skippedDuplicates += 1;
         }
      }
      toast.success(
        `Synced ${imported} new opportunities from ${getCivicCityName(cities.length ? cities : DEFAULT_CIVIC_CITIES, selectedCity)}${skippedDuplicates ? ` (${skippedDuplicates} duplicates skipped)` : ""}!`,
      );
    } catch (e) {
      toast.error("Failed to sync public data");
    } finally {
      setIsSyncing(false);
    }
  };

  const buildMapUrl = (opp: any) => {
    if (typeof opp.lat === "number" && typeof opp.lng === "number") {
      const params = new URLSearchParams({
        lat: String(opp.lat),
        lng: String(opp.lng),
        radius: "5",
        itemId: opp.id,
      });
      return `/map?${params.toString()}`;
    }

    return "/map";
  };

  const IGNORED_CATEGORIES = [
    "Parking Enforcement",
    "Graffiti",
    "Street and Sidewalk Cleaning",
    "Street Condition",
    "Encampments",
    "MUNI Feedback",
    "Litter Basket Complaint",
    "Rodent",
    "Sign Repair",
    "Streetlights"
  ];

  const civicFilteredOpps = opportunities.filter((opp) => {
    const isIgnored = IGNORED_CATEGORIES.some(cat => opp.notes?.includes(cat));
    const isNotAddress = opp.propertyAddress === "Unknown Location" || opp.propertyAddress.includes("Not associated with a specific address");
    if (isIgnored || isNotAddress) return false;

    return true;
  });

  const filteredOutCount = opportunities.length - civicFilteredOpps.length;

  const filteredOpps = civicFilteredOpps.filter((opp) => {
    return opp.propertyAddress.toLowerCase().includes(search.toLowerCase()) || 
      (opp.tags && opp.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase())));
  }).sort((a, b) => b.opportunityScore - a.opportunityScore);

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 pb-10">
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm shadow-slate-900/5">
        <div className="grid gap-6 px-5 py-6 sm:px-8 lg:grid-cols-[1.15fr_.85fr] lg:items-end lg:px-10 lg:py-8">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              <span>Signal discovery</span>
              <span className="h-1 w-1 rounded-full bg-slate-300" />
              <span>Civic intelligence</span>
            </div>
            <div className="space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Hidden Deal Signals</h1>
              <p className="max-w-2xl text-sm leading-6 text-slate-500">See nearby 311 complaints, code violations, abandoned vehicle reports, and housing maintenance issues around any property.</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-600">
              <span className="rounded-full bg-slate-100 px-3 py-1.5">{civicFilteredOpps.length} surfaced</span>
              <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-700">{filteredOpps.length} matching search</span>
              <span className="rounded-full bg-amber-50 px-3 py-1.5 text-amber-700">{filteredOutCount} filtered noise</span>
              <span className="rounded-full bg-blue-50 px-3 py-1.5 text-blue-700">{cities.length ? getCivicCityName(cities, selectedCity) : getCivicCityName(DEFAULT_CIVIC_CITIES, selectedCity)}</span>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-stretch">
            <div className="flex w-full sm:w-auto overflow-hidden rounded-2xl border border-emerald-200 bg-white shadow-[0_10px_30px_-18px_rgba(16,185,129,0.55)]">
              <Button
                variant="outline"
                onClick={handleSyncPublicData}
                disabled={isSyncing}
                className="h-auto min-h-12 w-full sm:w-auto rounded-none border-0 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-4 py-3 text-left text-white hover:from-emerald-500 hover:via-teal-500 hover:to-cyan-500"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
                    <Sparkles className={`w-4 h-4 ${isSyncing ? "animate-spin" : "text-white"}`} />
                  </span>
                  <span className="flex flex-col items-start leading-tight">
                    <span className="font-semibold">{isSyncing ? "Scanning civic signals" : "Scan Civic Signals"}</span>
                    <span className="text-[11px] text-emerald-50/90">311 complaints, violations, and distress clues</span>
                  </span>
                </span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger className="inline-flex h-auto min-h-12 w-12 items-center justify-center border-l border-emerald-200 bg-emerald-50 text-emerald-700 transition-colors hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30 disabled:opacity-50" disabled={isSyncing}>
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-56">
                  {cities.map((c) => (
                    <DropdownMenuItem key={c.id} onClick={() => setSelectedCity(c.id)}>
                      {c.name} {selectedCity === c.id && "✓"}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <Button variant="outline" className="gap-2 shrink-0 bg-white w-full sm:w-auto rounded-2xl border-slate-200 px-4 py-3 shadow-sm hover:bg-slate-50" onClick={handleImportClick} disabled={isImporting}>
              <Upload className={`w-4 h-4 ${isImporting ? "animate-pulse" : ""}`} /> {isImporting ? "Importing..." : "Import CSV"}
            </Button>
            <input ref={importInputRef} type="file" accept=".csv" className="hidden" onChange={handleImportCsv} />
            <AddOpportunityDialog>
              <Button className="w-full sm:w-auto gap-2 rounded-2xl bg-slate-950 px-4 py-3 font-medium shadow-sm hover:bg-slate-800">
                <Plus className="w-4 h-4" /> Add Opportunity
              </Button>
            </AddOpportunityDialog>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Surfaced</p>
              <p className="text-2xl font-bold text-slate-900">{civicFilteredOpps.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <Search className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400">Search matches</p>
              <p className="text-2xl font-bold text-slate-900">{filteredOpps.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-slate-400">Filtered noise</p>
              <p className="text-2xl font-bold text-slate-900">{filteredOutCount}</p>
              <p className="text-xs text-slate-500">Hidden by default</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600">
              <MapPin className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-slate-400">City scope</p>
              <p className="text-2xl font-bold text-slate-900 truncate">{cities.length ? getCivicCityName(cities, selectedCity) : getCivicCityName(DEFAULT_CIVIC_CITIES, selectedCity)}</p>
              <p className="text-xs text-slate-500">Civic signals feed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Live civic data</p>
              <p className="text-sm text-slate-500">This tool queries real-time public sector APIs directly. Refresh frequencies vary by municipality—for example, Baltimore updates daily, Louisville updates weekly, while others may update annually.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{selectedCity}</Badge>
              <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">{cities.length || DEFAULT_CIVIC_CITIES.length} cities</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {importSummary ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 sm:p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Latest CSV import summary</p>
              <p className="text-sm text-slate-500">{importSummary.imported} opportunities imported from {importSummary.scanned} scanned rows.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{importSummary.imported} imported</Badge>
              <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">{importSummary.scanned} scanned</Badge>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{importSummary.duplicates} duplicates skipped</Badge>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 flex items-center">
          <div className="relative flex-1 w-full">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search addresses or tags..."
              className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-sm placeholder:text-slate-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Popover>
            <PopoverTrigger className="absolute right-2 text-slate-400 hover:text-emerald-600 rounded-full h-8 w-8 flex items-center justify-center hover:bg-slate-100 transition-colors">
              <Info className="w-4 h-4" />
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-2rem)] sm:w-80 p-4" align="start">
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-slate-900 border-b pb-2">Example Search Queries</h4>
                <div className="grid gap-2 text-sm text-slate-600">
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-emerald-700">"abandoned"</span>
                    <span className="text-xs">Find vacant or abandoned properties</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-emerald-700">"code violation"</span>
                    <span className="text-xs">Discover code enforcement signals</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-emerald-700">"washington"</span>
                    <span className="text-xs">Search by city or tag name</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold text-emerald-700">"123 Main St"</span>
                    <span className="text-xs">Search for a specific address</span>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="hidden sm:block text-xs text-slate-500">
          {filteredOutCount > 0 ? `${filteredOutCount} civic-noise records hidden by default` : "All civic records are visible"}
        </div>
      </div>

      {filteredOpps.length === 0 ? (
        <Card className="border-dashed shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
              <Building className="w-8 h-8 text-emerald-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">No opportunities found</h3>
            <p className="text-slate-500 max-w-sm mb-6">
              Start building your real estate pipeline by importing lists or saving properties from the map view.
            </p>
            <div className="flex gap-3">
               <Link to="/map" className="inline-flex items-center gap-2 bg-blue-50 text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors">
                  <MapPin className="w-4 h-4" /> Open Map
               </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOpps.map((opp) => (
            <PropertySnapshotCard
              key={opp.id}
              tone="opportunity"
              className="h-full"
              address={opp.propertyAddress}
              sourceLabel={opp.source === "public_data" ? "Public Data" : opp.source === "csv" ? "CSV Import" : opp.source}
              signalLabel={`Score: ${opp.opportunityScore}`}
              summary={opp.notes || undefined}
              tags={opp.tags || []}
              metaLabel={formatDistanceToNow(parseISO(opp.savedAt)) + " ago"}
              primaryAction={{
                label: convertingId === opp.id ? "Converting..." : "Convert to Lead",
                disabled: convertingId === opp.id,
                onClick: (event) => handleConvertToLead(event, opp),
                icon: convertingId === opp.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : undefined,
              }}
              secondaryAction={{
                label: "Open on Map",
                onClick: () => {
                  navigate(buildMapUrl(opp));
                },
                icon: <MapPin className="w-4 h-4" />,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
